import { Box, CircularProgress, FormControlLabel, Link, Switch, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OpenInNew } from '@mui/icons-material';
import { EasTableWithoutFilter } from '../../../../../ui/EasTableWithoutFilter';
import { THandleAction, TTableStructure } from '../../../../../ui/EasTable/types';
import { getEmByIdFull } from '../../../../../redux/reducers/emManagement/selectors';
import {
	getEmLastMtdReportsRequest,
	getManualProcessingRequest,
	updateManualProcessingRequest,
} from '../../../../../redux/reducers/hmrc/reducer';
import {
	getEmLastMtdReports,
	getEmLastMtdReportsLoad,
	getManualProcessing,
	getManualProcessingLoad,
	getUpdateManualProcessingLoad,
} from '../../../../../redux/reducers/hmrc/selectors';
import { ConfirmationDialog } from '../../../../../ui/ConfirmationDialog';
import { IMtdEmReportsItem } from '../../../../../redux/reducers/hmrc/types';
import { PATHS } from '../../../../../routes/constants';
import { tableStructure } from './tableStructure';
import { emBoxesStructure } from '../../emBoxesStructure';

export const LastHmrcReports: FC = () => {
	const dispatch = useDispatch();
	const emDataFull = useSelector(getEmByIdFull);
	const emLastMtdReports = useSelector(getEmLastMtdReports);
	const emLastMtdReportsLoading = useSelector(getEmLastMtdReportsLoad);
	const manualProcessing = useSelector(getManualProcessing);
	const manualProcessingLoading = useSelector(getManualProcessingLoad);
	const updateManualProcessingLoading = useSelector(getUpdateManualProcessingLoad);
	const [openManualProcessingDialog, setOpenManualProcessingDialog] = useState(false);
	const lastHmrcReportsLink = emBoxesStructure(emDataFull).find(
		(box) => box.id === 'last-hmrc-reports',
	)?.link;
	useEffect(() => {
		if (emDataFull) {
			dispatch(getEmLastMtdReportsRequest({ pageSize: 3, searchString: emDataFull.fullName }));
		}
	}, [dispatch, emDataFull]);

	useEffect(() => {
		if (emDataFull?.id) {
			dispatch(getManualProcessingRequest(emDataFull.id));
		}
	}, [dispatch, emDataFull?.id]);

	const isManual = manualProcessing?.emId === emDataFull?.id && !!manualProcessing?.isManual;

	const handleConfirmManualProcessing = () => {
		if (emDataFull?.id) {
			dispatch(updateManualProcessingRequest({ emId: emDataFull.id, isManual: !isManual }));
		}
		setOpenManualProcessingDialog(false);
	};

	// Guard against data of a previously opened EM still being in the store
	const tableData =
		emLastMtdReports && emLastMtdReports.data?.every((item) => item.emId === emDataFull?.id)
			? emLastMtdReports
			: null;

	const handleAction: THandleAction = useCallback(() => {
		// No actions for this table
	}, []);

	const handleLink = useCallback((id: number) => {
		window.open(`${PATHS.HMRC_PAGE}/${id}`, '_blank');
	}, []);

	return (
		<EasTableWithoutFilter<IMtdEmReportsItem>
			tableStructure={
				tableStructure as unknown as TTableStructure<keyof IMtdEmReportsItem, IMtdEmReportsItem>[]
			}
			items={tableData}
			itemsIsLoading={emLastMtdReportsLoading}
			handleAction={handleAction}
			handleLink={handleLink}
			skeletonRows={[1]}
		>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Last HMRC VAT Reports
					</Typography>
					<Link
						href={lastHmrcReportsLink?.url}
						target="_blank"
						sx={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: 0.5,
							textDecoration: 'none',
						}}
					>
						<Typography
							variant="body1"
							color="primary"
							fontWeight="bold"
							sx={{ fontSize: '0.8rem' }}
						>
							{lastHmrcReportsLink?.label}
						</Typography>
						<OpenInNew sx={{ fontSize: '0.7rem', p: 0, m: 0 }} />
					</Link>
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
					{(emLastMtdReportsLoading ||
						manualProcessingLoading ||
						updateManualProcessingLoading) && <CircularProgress size={20} />}
					<FormControlLabel
						label={
							<Typography
								variant="body1"
								color="primary"
								fontWeight="bold"
								sx={{ fontSize: '0.8rem' }}
							>
								Manual Processing
							</Typography>
						}
						labelPlacement="start"
						sx={{ m: 0 }}
						control={
							<Switch
								size="small"
								checked={isManual}
								disabled={
									!emDataFull?.id || manualProcessingLoading || updateManualProcessingLoading
								}
								onChange={() => setOpenManualProcessingDialog(true)}
							/>
						}
					/>
				</Box>
				<ConfirmationDialog
					open={openManualProcessingDialog}
					setClose={() => setOpenManualProcessingDialog(false)}
					handleConfirm={handleConfirmManualProcessing}
					title={`${isManual ? 'Disable' : 'Enable'} HMRC Manual Processing`}
					text={`Are you sure you want to ${
						isManual ? 'disable' : 'enable'
					} HMRC manual processing for this EM?`}
				/>
			</Box>
		</EasTableWithoutFilter>
	);
};
